import React, { useEffect, useMemo, useState } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { resolveImageEntries } from '../../utils/entityImages';
import { monsterKind } from '../../utils/monsterKind';
import { questsServedBy } from '../../utils/npcQuests';
import { npcSheets, NPC_SHEET_SIZE, npcRectInBounds } from '../../data/guide/npcPhotoSheets';
import { entityImages } from '../../data/guide/entityImages';
import { npcs } from '../../data/guide/npcs';
import { NPC_RECONCILE_IDS, canonicalNpcId, isSpellingDifference, mergedIdsOf, npcDisplayName, npcIdentity } from '../../data/guide/npcIdentity';
import { adminConfirmation } from '../../data/guide/adminConfirmations';
import { isPermanentSpawn, resolveNpcLocation } from '../../utils/npcLocation';
import type { WorldData } from '../../types/world';
type SheetState = {
  status: 'cargando' | 'ok' | 'error';
  w?: number;
  h?: number;
};

/** IDs distintos con foto del staff marcada como NPC (el Robot 805 cuenta una vez aunque tenga cuatro ubicaciones). */
const staffNpcIds = Array.from(new Set(entityImages.filter(e => e.kind === 'npc' && e.monsterId !== undefined && e.image?.origin === 'staff').map(e => e.monsterId!)));

/** Panel del staff: cifras reales del paquete VANGUARD_NPC y de la clasificación NPC, calculadas en este navegador. */
export function NpcPhotoPackCheck({
  w
}: {
  w: WorldData;
}) {
  const {
    npcPack,
    byMonster
  } = useMemo(() => resolveImageEntries(w), [w]);
  const [run, setRun] = useState(false);
  const [sheets, setSheets] = useState<Record<number, SheetState>>({});
  useEffect(() => {
    if (!run) return;
    Object.entries(npcSheets).forEach(([id, url]) => {
      const n = Number(id);
      setSheets(s => ({
        ...s,
        [n]: {
          status: 'cargando'
        }
      }));
      const img = new Image();
      img.onload = () => setSheets(s => ({
        ...s,
        [n]: {
          status: 'ok',
          w: img.naturalWidth,
          h: img.naturalHeight
        }
      }));
      img.onerror = () => setSheets(s => ({
        ...s,
        [n]: {
          status: 'error'
        }
      }));
      img.src = url;
    });
  }, [run]);
  const nuevas = npcPack.filter(r => r.status === 'nueva');
  const conservadas = npcPack.filter(r => r.status === 'conservada');
  const fuera = npcPack.filter(r => r.status === 'fuera de catálogo');
  const badRect = npcPack.filter(r => !npcRectInBounds(r.photo));
  const loaded = Object.values(sheets).filter(s => s.status === 'ok').length;
  const failed = Object.entries(sheets).filter(([, s]) => s.status === 'error').map(([k]) => Number(k));
  const differs = npcPack.filter(r => r.catalogName && r.catalogName.trim().toLowerCase() !== r.photo.sourceName.trim().toLowerCase());
  const spelling = differs.filter(r => isSpellingDifference(r.catalogName!, r.photo.sourceName));
  const nameDiff = differs.filter(r => !isSpellingDifference(r.catalogName!, r.photo.sourceName));
  const allNpcs = Array.from(w.monsters.values()).filter(m => monsterKind(w, m) === 'npc');
  const staffKept = staffNpcIds.filter(id => byMonster.get(id)?.image?.origin === 'staff');
  const withPhoto = new Set([...nuevas.map(r => r.photo.id), ...staffKept]);

  // Conteos de NPC (entidades, no imágenes). 234 → 226 cuenta una vez; el Robot 805 cuenta una vez con sus cuatro ubicaciones.
  const published = allNpcs.filter(m => canonicalNpcId(m.id) === m.id);
  const pubWithImg = published.filter(m => !!byMonster.get(m.id)?.image);
  const perMap = new Map<string, {
    npcs: Set<number>;
    locations: number;
  }>();
  const addLoc = (mapName: string, id: number) => {
    const cur = perMap.get(mapName) ?? {
      npcs: new Set<number>(),
      locations: 0
    };
    cur.npcs.add(id);
    cur.locations++;
    perMap.set(mapName, cur);
  };
  const guideById = new Map<number, typeof npcs>();
  npcs.forEach(n => n.monsterId !== undefined && guideById.set(n.monsterId, [...(guideById.get(n.monsterId) ?? []), n]));
  let withoutLocation = 0;
  published.forEach(m => {
    const guide = guideById.get(m.id);
    let points = 0;
    if (guide) {
      guide.forEach(n => {
        const loc = resolveNpcLocation(n, {
          status: 'ready',
          data: w
        });
        if (loc.state === 'admin' || loc.state === 'config') loc.points.forEach(p => (addLoc(p.mapName, m.id), points++));
      });
    } else {
      ;
      [m.id, ...mergedIdsOf(m.id)].flatMap(id => w.idx.spawnsByMonster.get(id) ?? []).filter(isPermanentSpawn).forEach(s => (addLoc(w.maps.get(s.mapId)?.name ?? `Mapa ${s.mapId}`, m.id), points++));
    }
    if (!points) withoutLocation++;
  });
  const mapRows = Array.from(perMap.entries()).sort((a, b) => b[1].npcs.size - a[1].npcs.size || a[0].localeCompare(b[0], 'es'));
  const totalLocations = mapRows.reduce((s, [, v]) => s + v.locations, 0);
  const reconcile = NPC_RECONCILE_IDS.map(id => {
    const m = w.monsters.get(id);
    const ident = npcIdentity(id);
    const img = byMonster.get(id)?.image;
    const guideNpc = npcs.find(n => n.monsterId === id);
    const spawns = w.idx.spawnsByMonster.get(id) ?? [];
    const quests = questsServedBy(w, id);
    const publication = !m ? 'no está en 06_monsters' : ident?.mergedInto !== undefined ? `unificado con ${npcs.find(n => n.monsterId === ident.mergedInto)?.name ?? ident.mergedInto} (${ident.mergedInto}) por el staff: sin ficha duplicada` : 'publicado como NPC';
    return {
      id,
      name: m ? npcDisplayName(id, m.name) : ident?.indexName ?? '—',
      catalogName: m?.name,
      classification: m ? monsterKind(w, m) === 'npc' ? 'NPC (identidad indicada por el staff)' : 'MONSTRUO — mal clasificado' : '—',
      publication,
      exclusion: 'sin exclusión aprobada',
      photo: img ? img.origin === 'staff' ? 'foto del staff' : 'foto del paquete' : 'pendiente de imagen (marcador neutro)',
      location: ident?.mergedInto !== undefined ? 'ver ficha unificada' : spawns.length ? `${spawns.length} apariciones en 08_spawns` : 'no documentada · falta NPC_ORIGEN: mapa y X/Y',
      role: guideNpc ? guideNpc.role : quests.length ? 'atiende misiones (37_quest_rules)' : 'no documentada · falta función en TIENDAS_SERVICIOS',
      inventory: guideNpc?.services?.some(s => s.kind === 'venta') ? 'venta indicada; piezas pendientes' : 'no documentado · falta TIENDAS_SERVICIOS: ítem, variante, precio y moneda'
    };
  });
  const report = [`IDs del paquete VANGUARD_NPC: ${npcPack.length} · en 06_monsters: ${npcPack.length - fuera.length} · fuera del catálogo: ${fuera.length}`, `Fotos nuevas del paquete: ${nuevas.length} · conservadas (ya tenían foto): ${conservadas.length}${conservadas.length ? ` — ${conservadas.map(r => `${r.photo.id} ${r.catalogName}`).join(', ')}` : ''}`, `Fotos del staff de NPC (IDs distintos): ${staffKept.length} — ${staffKept.map(id => `${w.monsters.get(id)?.name ?? id} (${id})`).join(', ')}`, `NPC con foto: ${nuevas.length} del paquete + ${staffKept.length} del staff = ${withPhoto.size} IDs (Robot 805 cuenta una vez; sus cuatro ubicaciones no son entidades distintas)`, `Láminas que cargan: ${run ? `${loaded}/3` : 'sin comprobar'} · recortes dentro de su celda: ${npcPack.length - badRect.length}/${npcPack.length}`, '', '## Conteos de NPC (entidades, no imágenes)', `IDs de NPC en los archivos originales: ${allNpcs.length}`, `NPC únicos publicados (unificación 234 → 226 aplicada): ${published.length}`, `Publicados con imagen: ${pubWithImg.length} · sin imagen (marcador neutro): ${published.length - pubWithImg.length}`, `Ubicaciones documentadas o confirmadas: ${totalLocations} · NPC sin ubicación: ${withoutLocation}`, ...mapRows.map(([map, v]) => `  ${map}: ${v.npcs.size} fichas · ${v.locations} ubicaciones`), `Unificación Trainer/Pet Trainer: ${adminConfirmation('npc-identity:234=226') ? 'confirmada por administración (11/10/2026)' : 'sin confirmar'} · 234 en archivos: ${w.monsters.has(234) ? 'sí' : 'no'} · ficha de 234 publicada: ${published.some(m => m.id === 234) ? 'SÍ (error)' : 'no'}`, '', '## Nombres (identidad por ID; la comprobación visual es independiente)', ...spelling.map(r => `${r.photo.id}\tlámina "${r.photo.sourceName}" · catálogo "${r.catalogName}"\tdiferencia de escritura (truncado); se muestra "${npcDisplayName(r.photo.id, r.catalogName!)}" y se conserva el del catálogo como alias`), ...nameDiff.map(r => `${r.photo.id}\tlámina "${r.photo.sourceName}" · catálogo "${r.catalogName}"\tnombre distinto: revisar identidad`), `Comprobación visual de las ${nuevas.length} fotos nuevas: pendiente (no se ejecutó en el navegador).`, '', '## NPC a reconciliar (foto, ubicación, función e inventario con estados independientes)', ...reconcile.map(r => `${r.id}\t${r.name}${r.catalogName && r.catalogName !== r.name ? ` (catálogo: ${r.catalogName})` : ''}\n  clasificación: ${r.classification}\n  publicación: ${r.publication} · exclusión: ${r.exclusion}\n  foto: ${r.photo}\n  ubicación: ${r.location}\n  función: ${r.role}\n  inventario: ${r.inventory}`), '', ...fuera.map(r => `${r.photo.id}\t${r.photo.sourceName}\tno existe en 06_monsters: no se muestra`), ...badRect.map(r => `${r.photo.id}\t${r.photo.sourceName}\trecorte fuera de su celda`), ...failed.flatMap(s => npcPack.filter(r => r.photo.sheet === s).map(r => `${r.photo.id}\t${r.photo.sourceName}\tla lámina ${s} no cargó`)), ...Object.entries(sheets).filter(([, s]) => s.status === 'ok' && (s.w !== s.h || (s.w ?? 0) !== NPC_SHEET_SIZE)).map(([k, s]) => `Lámina ${k}\t${s.w}×${s.h}\tno mide 1536 × 1536: el recorte se escala proporcionalmente`)];
  return <section aria-labelledby="npc-pack" className="mt-8" data-id="element-1699" data-mp="d0j2ncotnhbz">
      <h3 id="npc-pack" className="text-lg font-semibold text-white" data-id="element-1700" data-mp="qwo8ex8yyjgu">Paquete VANGUARD_NPC</h3>
      <p className="mt-1 text-sm text-gray-400" data-id="element-1701" data-mp="lzxtwectbvxj">Índice JSON no recibido: recortes medidos sobre las láminas; procedencia y retoque pendientes por foto.</p>
      <div className="mt-3 flex flex-wrap gap-2" data-id="element-1702" data-mp="2nq98xst3vcrt">
        <button type="button" onClick={() => setRun(true)} className="h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-1703" data-mp="2nrj7sk7s8ozi">
          Comprobar carga de las láminas NPC
        </button>
        <CopyTextButton text={report.join('\n')} label="Copiar comprobación NPC" data-id="element-1704" data-mp="1qn3v8md4zsy8" />
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4" data-id="element-1705" data-mp="1lospampyq2la">
        {[['IDs NPC en archivos', allNpcs.length], ['NPC únicos publicados', published.length], ['Con imagen / sin imagen', `${pubWithImg.length} / ${published.length - pubWithImg.length}`], ['Ubicaciones · mapas', `${totalLocations} · ${mapRows.length}`]].map(([label, value]) => <div key={label as string} data-id="element-1706" data-mp="24o6llbb5zpey">
            <dt className="text-xs text-gray-400" data-id="element-1707" data-mp="24n4xa55516fa">{label}</dt>
            <dd className="font-mono text-lg font-semibold text-white" data-id="element-1708" data-mp="3230v35mijxa8">{value}</dd>
          </div>)}
      </dl>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10" data-id="element-1709" data-mp="2xbakqznkt9it">
        <table className="w-full min-w-[760px] text-left text-sm" data-id="element-1710" data-mp="34o084kxk6pc3">
          <caption className="sr-only" data-id="element-1711" data-mp="39ma95c5ysk5m">NPC a reconciliar</caption>
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1712" data-mp="39mjx6u7g8h8o">
            <tr data-id="element-1713" data-mp="3ekbnxkwcxxba">
              <th className="px-3 py-2 font-semibold" data-id="element-1714" data-mp="3ejtlg6j3qmux">ID · NPC</th>
              <th className="px-3 py-2 font-semibold" data-id="element-1715" data-mp="3in8dxwo88vp9">Publicación</th>
              <th className="px-3 py-2 font-semibold" data-id="element-1716" data-mp="3nm2ust2mwa6q">Foto</th>
              <th className="px-3 py-2 font-semibold" data-id="element-1717" data-mp="3nljl1zn50s3s">Ubicación</th>
              <th className="px-3 py-2 font-semibold" data-id="element-1718" data-mp="3ske2oy9dcamb">Función</th>
              <th className="px-3 py-2 font-semibold" data-id="element-1719" data-mp="16i3emd31yfw">Inventario</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 align-top text-gray-300" data-id="element-1720" data-mp="65l8u7wz66oe">
            {reconcile.map(r => <tr key={r.id} data-id="element-1721" data-mp="a7ont6iurtef">
                <td className="px-3 py-2" data-id="element-1722" data-mp="a8hkfeo3l2iu">
                  <span className="font-mono text-white" data-id="element-1723" data-mp="f69d4awkgso3">{r.id}</span> {r.name}
                  <span className="block text-xs text-gray-500" data-id="element-1724" data-mp="f46pfxivzbmj">{r.classification}</span>
                </td>
                <td className="px-3 py-2" data-id="element-1725" data-mp="2b71o5htkh6n6">{r.publication}</td>
                <td className="px-3 py-2" data-id="element-1726" data-mp="2793xeak5echo">{r.photo}</td>
                <td className="px-3 py-2" data-id="element-1727" data-mp="2kyxn0v3h90sg">{r.location}</td>
                <td className="px-3 py-2" data-id="element-1728" data-mp="2l0ihyltpelqu">{r.role}</td>
                <td className="px-3 py-2" data-id="element-1729" data-mp="2g7z3znr3toli">{r.inventory}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-white/[0.03] p-3 text-xs text-gray-300" data-id="element-1730" data-mp="2uvtx3590xuz4">{report.join('\n')}</pre>
      <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-8" data-id="element-1731" data-mp="33xthm7j8zh6t">
        {nuevas.map(r => {
        const name = npcDisplayName(r.photo.id, r.catalogName ?? r.photo.sourceName);
        return <li key={r.photo.id} className="text-center" data-id="element-1732" data-mp="19j91m85vg9tr">
              <EntityImage entry={byMonster.get(r.photo.id)} alt={name} name={name} size="card" kind="persona" data-id="element-1733" data-mp="19lvhqw6tvxnl" />
              <span className="mt-1 block truncate text-xs text-gray-400" data-id="element-1734" data-mp="3g8b3j5odrhd9">{r.photo.id} · {name}</span>
            </li>;
      })}
      </ul>
    </section>;
}